import { getNetPrice, isPromoCoupon } from "../config/plans"
import { isHalloweenCoupon } from "./halloween-offer"

export interface RecurringCouponOffer {
  billingMode: "subscription"
  renewalAmount: number
  discountDuration: "subscription_lifetime"
  expiresAt: string
}

export interface CouponValidation {
  discountAmount: number
  message?: string
  recurringOffer?: RecurringCouponOffer
}

interface CouponSnapshot {
  couponCode: string
  appliedCoupon: string | null
  discountAmount: number
  totalPayable: number
  couponError: string
  couponMessage: string
  isValidatingCoupon: boolean
  isSubmitting: boolean
  recurringOffer?: RecurringCouponOffer
}

interface CouponOptions {
  orderValue: number
  advertisedAmount?: number
  promoCode: string
  validate?: (code: string, signal: AbortSignal) => Promise<CouponValidation>
}

/** One source of truth for the displayed discount and the code sent to checkout. */
export class CheckoutCouponController {
  private state: CouponSnapshot
  private listeners = new Set<() => void>()
  private generation = 0
  private request: AbortController | null = null
  private pristine = true

  constructor(private options: CouponOptions) {
    this.state = {
      couponCode: options.promoCode,
      appliedCoupon: null,
      discountAmount: 0,
      totalPayable: options.orderValue,
      couponError: "",
      couponMessage: "",
      isValidatingCoupon: Boolean(options.promoCode && options.orderValue > 0),
      isSubmitting: false,
    }
  }

  getSnapshot = () => this.state

  setValidator = (validate: NonNullable<CouponOptions["validate"]>) => {
    this.options = { ...this.options, validate }
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private update(next: Partial<CouponSnapshot>) {
    this.state = { ...this.state, ...next }
    this.listeners.forEach((listener) => listener())
  }

  cancel = () => {
    this.generation += 1
    this.request?.abort()
    this.request = null
  }

  change = (value: string, inputType?: string) => {
    if (this.state.isSubmitting) return
    this.pristine = false
    if (
      this.state.appliedCoupon &&
      (inputType === "deleteContentBackward" || inputType === "deleteContentForward")
    ) {
      value = ""
    }
    this.cancel()
    this.update({
      couponCode: value.trim(),
      appliedCoupon: null,
      discountAmount: 0,
      totalPayable: this.options.orderValue,
      couponError: "",
      couponMessage: "",
      isValidatingCoupon: false,
      recurringOffer: undefined,
    })
  }

  remove = () => this.change("")

  removeOnKey = (key: string) => {
    if (!this.state.appliedCoupon || (key !== "Backspace" && key !== "Delete")) return false
    this.remove()
    return true
  }

  start = async () => {
    if (this.pristine && this.options.promoCode && this.options.orderValue > 0)
      await this.apply(false)
  }

  apply = async (showSuccess = true) => {
    if (this.state.isSubmitting || !this.options.validate) return
    if (showSuccess) this.pristine = false
    const code = this.state.couponCode.trim()
    if (!code) {
      this.update({ couponError: "Please enter a coupon code" })
      return
    }
    this.cancel()
    const generation = this.generation
    const request = new AbortController()
    this.request = request
    this.update({ isValidatingCoupon: true, couponError: "", couponMessage: "" })
    try {
      const result = await this.options.validate(code, request.signal)
      if (generation !== this.generation) return
      if (!Number.isFinite(result.discountAmount) || result.discountAmount < 0) {
        throw new Error("Failed to validate coupon. Please try again.")
      }
      const rawNet = getNetPrice(this.options.orderValue, result.discountAmount)
      const totalPayable =
        isPromoCoupon(code) && typeof this.options.advertisedAmount === "number"
          ? Math.min(rawNet, this.options.advertisedAmount)
          : rawNet
      if (
        isHalloweenCoupon(code) &&
        (result.recurringOffer?.billingMode !== "subscription" ||
          result.recurringOffer.discountDuration !== "subscription_lifetime" ||
          result.recurringOffer.renewalAmount !== totalPayable ||
          !Number.isFinite(Date.parse(result.recurringOffer.expiresAt)) ||
          Date.parse(result.recurringOffer.expiresAt) <= Date.now())
      )
        throw new Error("The recurring Halloween offer could not be confirmed. Please try again.")
      this.update({
        appliedCoupon: code,
        discountAmount: this.options.orderValue - totalPayable,
        totalPayable,
        recurringOffer: result.recurringOffer,
        couponMessage: showSuccess ? result.message || "Coupon applied successfully!" : "",
      })
    } catch (error) {
      if (generation !== this.generation) return
      this.update({
        appliedCoupon: null,
        discountAmount: 0,
        totalPayable: this.options.orderValue,
        recurringOffer: undefined,
        couponError: error instanceof Error ? error.message : "Failed to validate coupon",
      })
    } finally {
      if (generation === this.generation) {
        this.request = null
        this.update({ isValidatingCoupon: false })
      }
    }
  }

  hideMessage = () => this.update({ couponMessage: "" })

  expireOffer = () => {
    if (!this.state.recurringOffer || Date.parse(this.state.recurringOffer.expiresAt) > Date.now())
      return false
    this.rejectCoupon("The Halloween offer has expired.")
    return true
  }

  canSubmit = () =>
    !this.state.isValidatingCoupon &&
    !this.state.isSubmitting &&
    (!this.state.recurringOffer || Date.parse(this.state.recurringOffer.expiresAt) > Date.now()) &&
    (!this.state.couponCode || this.state.appliedCoupon === this.state.couponCode)

  beginCheckout = () => {
    if (this.expireOffer()) return null
    if (!this.canSubmit()) return null
    const snapshot = {
      couponCode: this.state.appliedCoupon ?? "",
      totalPayable: this.state.totalPayable,
    }
    this.update({ isSubmitting: true })
    return snapshot
  }

  finishCheckout = () => this.update({ isSubmitting: false })

  rejectCoupon = (message: string) => {
    this.cancel()
    this.update({
      appliedCoupon: null,
      discountAmount: 0,
      totalPayable: this.options.orderValue,
      couponError: message,
      couponMessage: "",
      isValidatingCoupon: false,
      recurringOffer: undefined,
    })
  }
}
